import { GetProfileMedicationsDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  MEDICATION_DURATION_OPTIONS,
  MEDICATION_FORM_OPTIONS,
  MEDICATION_FREQUENCY_OPTIONS,
  MEDICATION_STATUS_OPTIONS,
  MEDICATION_STOP_REASON_OPTIONS,
  MEDICATION_STRENGTH_UNIT_OPTIONS,
  MedicationOption,
  OTHER_OPTION,
} from './medicationFormOptions';

/**
 * A column with nothing to say says nothing. A dash reads as a value, and a
 * table of them is harder to scan than a table of gaps.
 */
const EMPTY = '';

function labelOf<T extends string>(
  options: MedicationOption<T>[],
  value?: T,
): string | undefined {
  return options.find((option) => option.value === value)?.label;
}

/** "5,4 mg", or the vet's own unit when it was off the list. */
export function formatStrength(medication: GetProfileMedicationsDto): string {
  if (medication.strengthValue === undefined) return EMPTY;
  const unit =
    medication.strengthUnit === OTHER_OPTION
      ? medication.strengthUnitCustom
      : medication.strengthUnit === 'NONE'
      ? ''
      : labelOf(MEDICATION_STRENGTH_UNIT_OPTIONS, medication.strengthUnit);
  return [medication.strengthValue, unit].filter(Boolean).join(' ');
}

/** "½ tabletki" — how much goes in, in what. */
export function formatDose(
  medication: GetProfileMedicationsDto,
): string | undefined {
  if (medication.dosagePending) {
    return translateKey('medication.table.dosagePending');
  }

  const form =
    medication.form === OTHER_OPTION
      ? medication.formCustom
      : labelOf(MEDICATION_FORM_OPTIONS, medication.form);

  const quantity =
    medication.quantityCustom ||
    (medication.quantityValue !== undefined
      ? String(medication.quantityValue)
      : undefined);

  return [quantity, form].filter(Boolean).join(' ') || undefined;
}

/** "2 razy dziennie", or the vet's own wording when it was off the list. */
export function formatFrequency(
  medication: GetProfileMedicationsDto,
): string | undefined {
  if (medication.dosagePending) return undefined;
  if (medication.frequencyType === OTHER_OPTION) {
    return medication.frequencyCustom || undefined;
  }
  return (
    [
      medication.frequencyValue,
      labelOf(MEDICATION_FREQUENCY_OPTIONS, medication.frequencyType),
    ]
      .filter((part) => part !== undefined && part !== '')
      .join(' ') || undefined
  );
}

/**
 * Both halves on one line, for the places that have a line rather than a cell
 * — the summary step and the visit card.
 */
export function formatDosing(medication: GetProfileMedicationsDto): string {
  const line = [formatDose(medication), formatFrequency(medication)]
    .filter(Boolean)
    .join(', ');
  return line || medication.dosageSchedule || EMPTY;
}

/** "7 dni", "do odwołania", or whatever the vet wrote instead. */
export function formatDuration(medication: GetProfileMedicationsDto): string {
  if (!medication.durationType) return EMPTY;
  if (medication.durationType === OTHER_OPTION) {
    return medication.durationCustom || EMPTY;
  }
  const label = labelOf(MEDICATION_DURATION_OPTIONS, medication.durationType);
  return [medication.durationValue, label].filter(Boolean).join(' ') || EMPTY;
}

export function formatStatus(medication: GetProfileMedicationsDto): string {
  return (
    labelOf(MEDICATION_STATUS_OPTIONS, medication.status) ?? medication.status
  );
}

/** Why it was stopped, with the vet's own wording when the list had no entry. */
export function formatStopReason(
  medication: GetProfileMedicationsDto,
): string | undefined {
  if (!medication.stopReason) return undefined;
  if (medication.stopReason === OTHER_OPTION) {
    return medication.stopReasonNote || undefined;
  }
  return labelOf(MEDICATION_STOP_REASON_OPTIONS, medication.stopReason);
}

export function formatDate(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? undefined
    : date.toLocaleDateString('pl-PL');
}

export const EMPTY_CELL = EMPTY;
